import { Box } from '@mui/material';
import { Button, colors, IconButton } from '@sweep-io/sweep-design';
import { Delete } from '@sweep-io/sweep-design/dist/icons';
import { WeightedGroupRef } from '../../../types/AssignmentsAssignTo';
import { SearchSelect } from '../../common/search-select/SearchSelect';
import { IntegerPicker } from '../../pages/assignments/group-members/IntegerPicker';
import { Percentage } from '../../common/Percentage';
import { useWeightedGroupsSelector } from './useWeightedGroupsSelector';

interface GroupOption {
  value: string;
  label: string;
}

interface WeightedGroupsSelectorProps {
  weightedGroups: WeightedGroupRef[];
  onChange: (weightedGroups: WeightedGroupRef[]) => void;
  groupOptions: GroupOption[];
  readonly?: boolean;
  isLoading?: boolean;
  onOpenDropdown?: () => void;
}

const GROUP_DROPDOWN_WIDTH = '200px';

export const WeightedGroupsSelector = ({
  weightedGroups,
  onChange,
  groupOptions,
  readonly,
  isLoading,
  onOpenDropdown,
}: WeightedGroupsSelectorProps) => {
  const {
    showAddPicker,
    showWeightsAndDelete,
    totalWeight,
    availableOptionsForAdd,
    getAvailableOptions,
    getDisplayValue,
    handleGroupChange,
    handleWeightChange,
    handleDeleteGroup,
    handleAddGroupClick,
    handleAddGroupSelect,
    handleAddPickerClose,
  } = useWeightedGroupsSelector({
    weightedGroups,
    onChange,
    groupOptions,
    onOpenDropdown,
  });

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {weightedGroups.map((group, index) => {
        const percentage = Math.round((group.weight / (totalWeight || 1)) * 100);
        return (
          <Box key={index} display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: GROUP_DROPDOWN_WIDTH,
                minWidth: GROUP_DROPDOWN_WIDTH,
                maxWidth: GROUP_DROPDOWN_WIDTH,
                '& .MuiFormControl-root': {
                  width: '100%',
                },
              }}
            >
              <SearchSelect
                isLoading={isLoading}
                searchPlaceholder="Search groups"
                SelectProps={{
                  placeholder: 'Choose value',
                  value: group.groupId || '',
                  disabled: readonly,
                  renderValue: () => getDisplayValue(group),
                  sx: { width: '100%' },
                }}
                onOpenCb={onOpenDropdown}
                items={getAvailableOptions(group.groupId)}
                onChange={(item) => handleGroupChange(index, item.value, item.label)}
              />
            </Box>

            {showWeightsAndDelete && (
              <>
                {/* Combined weight input + percentage container */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: `1px solid ${colors.grey[300]}`,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    height: '40px',
                    // Override IntegerPicker's TextField styling to be borderless as requested by designer
                    '& .MuiTextField-root': {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                        height: '34px',
                        minHeight: '34px',
                        width: '60px',
                      },
                    },
                  }}
                >
                  <IntegerPicker
                    value={group.weight}
                    onChange={(weight) => handleWeightChange(index, weight)}
                    disabled={readonly}
                    min={1}
                  />
                  <Box sx={{ width: '66px', mr: 1.5 }}>
                    <Percentage preCalculated={percentage} active typographyVariant="caption" />
                  </Box>
                </Box>

                <IconButton
                  variant="flat"
                  size="small"
                  disabled={readonly}
                  onClick={() => handleDeleteGroup(index)}
                >
                  <Delete />
                </IconButton>
              </>
            )}
          </Box>
        );
      })}

      {!readonly && availableOptionsForAdd.length > 0 && (
        <Box position="relative">
          {showAddPicker ? (
            <Box
              sx={{
                width: GROUP_DROPDOWN_WIDTH,
                minWidth: GROUP_DROPDOWN_WIDTH,
                maxWidth: GROUP_DROPDOWN_WIDTH,
                '& .MuiFormControl-root': {
                  width: '100%',
                },
              }}
            >
              <SearchSelect
                isLoading={isLoading}
                searchPlaceholder="Search groups"
                SelectProps={{
                  placeholder: 'Choose value',
                  value: '',
                  disabled: readonly,
                  open: true,
                  onClose: handleAddPickerClose,
                  sx: { width: '100%' },
                }}
                items={availableOptionsForAdd}
                onChange={(item) => handleAddGroupSelect(item.value, item.label)}
              />
            </Box>
          ) : (
            <Button
              onClick={handleAddGroupClick}
              size="tiny"
              startIconName="Plus"
              variant="flat-blue"
            >
              Add Group
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
